import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCompetition } from '../../contexts/CompetitionContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'react-hot-toast';
import { Loader2, Phone, Shield, CheckCircle, Mail, RefreshCw } from 'lucide-react';
import axios from 'axios';

const Container = styled.div`
  max-width: 450px;
  background-color: var(--white, #ffffff);
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 2rem;
  width: 90%;
  margin: 2rem auto;
`;

const Title = styled.h1`
  color: var(--primary, #7e22ce);
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--text-light, #94a3b8);
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-light, #94a3b8);
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light, #a855f7);
  }
`;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.75rem;
`;

const OTPContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin: 1rem 0;
`;

const OTPInput = styled.input`
  width: 3rem;
  height: 3rem;
  text-align: center;
  font-size: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light, #a855f7);
  }
`;

const TermsContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const TermsCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const OTPMessage = styled.p`
  color: var(--text-light, #94a3b8);
  text-align: center;
  margin: 1rem 0;
  font-size: 0.875rem;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: var(--primary, #7e22ce);
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  margin-top: 0.5rem;
  
  &:disabled {
    color: var(--text-light, #94a3b8);
    cursor: not-allowed;
  }
`;

const CompeteRegister: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState<'details' | 'otp' | 'terms'>('details');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    otp: '',
    agreedToTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.firstName || !formData.lastName || !formData.phoneNumber || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Validate phone number format (Kenyan format)
    const phoneRegex = /^(07\d{8}|\+2547\d{8})$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('Please enter a valid Kenyan phone number (e.g., 07XXXXXXXX or +2547XXXXXXXX)');
      return;
    }

    setLoading(true);
    try {
      // Simulate OTP sending
      toast.success('OTP sent successfully! (Demo mode)');
      setStep('otp');
      setOtpSent(true);
      setResendTimer(60);
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.otp || formData.otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // Simulate OTP verification
      toast.success('OTP verified successfully! (Demo mode)');
      setStep('terms');
    } catch (err: any) {
      setError(err.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleTermsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.agreedToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      // Register the user
      const registrationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        role: 'competitor'
      };

      const response = await register(registrationData);
      toast.success('Registration successful! Please log in to continue.');
      // Redirect to login page instead of dashboard to ensure captcha is solved
      navigate('/auth/login?registrationSuccess=true&role=competitor');
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    try {
      // Simulate resending OTP
      toast.success('OTP resent successfully! (Demo mode)');
      setResendTimer(60);
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const renderDetailsForm = () => (
    <form onSubmit={handleDetailsSubmit}>
      <FormGroup>
        <Label htmlFor="firstName">First Name</Label>
        <InputContainer>
          <InputIcon>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </InputIcon>
          <StyledInput
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter your first name"
            required
          />
        </InputContainer>
      </FormGroup>

      <FormGroup>
        <Label htmlFor="lastName">Last Name</Label>
        <InputContainer>
          <InputIcon>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </InputIcon>
          <StyledInput
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter your last name"
            required
          />
        </InputContainer>
      </FormGroup>

      <FormGroup>
        <Label htmlFor="phoneNumber">Phone Number</Label>
        <InputContainer>
          <InputIcon>
            <Phone size={20} />
          </InputIcon>
          <StyledInput
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="Enter your phone number (e.g., 07XXXXXXXX)"
            required
          />
        </InputContainer>
      </FormGroup>

      <FormGroup>
        <Label htmlFor="password">Password</Label>
        <InputContainer>
          <InputIcon>
            <Shield size={20} />
          </InputIcon>
          <StyledInput
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Create a password"
            required
          />
        </InputContainer>
      </FormGroup>

      <FormGroup>
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <InputContainer>
          <InputIcon>
            <Shield size={20} />
          </InputIcon>
          <StyledInput
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your password"
            required
          />
        </InputContainer>
      </FormGroup>

      <Button type="submit" disabled={loading}>
        {loading ? <Loader2 className="animate-spin" /> : 'Continue'}
      </Button>
    </form>
  );

  const renderOTPForm = () => (
    <form onSubmit={handleOTPSubmit}>
      <OTPMessage>
        We've sent a 6-digit code to {formData.phoneNumber}
      </OTPMessage>

      <OTPContainer>
        <OTPInput
          type="text"
          maxLength={6}
          value={formData.otp}
          onChange={(e) => setFormData(prev => ({ ...prev, otp: e.target.value }))}
          placeholder="000000"
        />
      </OTPContainer>

      <ResendButton
        type="button"
        onClick={handleResendOTP}
        disabled={resendTimer > 0}
      >
        {resendTimer > 0
          ? `Resend code in ${resendTimer}s`
          : 'Resend code'}
      </ResendButton>

      <Button type="submit" disabled={loading}>
        {loading ? <Loader2 className="animate-spin" /> : 'Verify OTP'}
      </Button>
    </form>
  );

  const renderTermsForm = () => (
    <form onSubmit={handleTermsSubmit}>
      <TermsContainer>
        <h3>Terms and Conditions</h3>
        <p>By registering as a competitor, you agree to:</p>
        <ul>
          <li>Provide accurate and truthful information</li>
          <li>Follow fair play rules during competitions</li>
          <li>Accept the risk of losing your stake in competitions</li>
          <li>Maintain appropriate behavior and sportsmanship</li>
          <li>Not engage in any form of cheating or manipulation</li>
        </ul>
      </TermsContainer>

      <TermsCheckbox>
        <input
          type="checkbox"
          id="agreedToTerms"
          name="agreedToTerms"
          checked={formData.agreedToTerms}
          onChange={handleChange}
          required
        />
        <label htmlFor="agreedToTerms">
          I agree to the terms and conditions
        </label>
      </TermsCheckbox>

      <Button type="submit" disabled={loading}>
        {loading ? <Loader2 className="animate-spin" /> : 'Complete Registration'}
      </Button>
    </form>
  );

  return (
    <Container>
      <Title>Competitor Registration</Title>
      <Subtitle>
        {step === 'details' && 'Create your competitor account'}
        {step === 'otp' && 'Verify your phone number'}
        {step === 'terms' && 'Review and accept terms'}
      </Subtitle>

      {error && <ErrorText>{error}</ErrorText>}

      {step === 'details' && renderDetailsForm()}
      {step === 'otp' && renderOTPForm()}
      {step === 'terms' && renderTermsForm()}
    </Container>
  );
};

export default CompeteRegister; 